import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BackendResponse,
  Datastore,
  Invoice,
  Milestone,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontWeight } from '@freelancer/ui/text';
import { InvoiceStatusApi } from 'api-typings/payments/payments';
import {
  BidCompleteStatusApi,
  MilestoneStatusApi,
} from 'api-typings/projects/projects';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';

enum Reason {
  NOT_REQUIRED = 'My requirements have changed and I no longer require this work to be done',
  COMPLETE = 'I am satisfied that all my project requirements have been met',
  INCOMPLETE = 'The freelancer is unable to complete my project',
}

@Component({
  selector: `app-project-end-modal`,
  template: `
    <fl-heading
      i18n="End project title."
      [size]="HeadingSize.XSMALL"
      [headingType]="HeadingType.H4"
      [flMarginBottom]="Margin.SMALL"
    >
      End Project
    </fl-heading>
    <fl-text i18n="End project confirmation text" [flMarginBottom]="Margin.MID">
      Ending this project will notify the freelancer that no further work is
      required for this project.
    </fl-text>
    <fl-bit *ngIf="!(hasOutstandingPayments$ | async)">
      <fl-text
        i18n="Description for end project options."
        [flMarginBottom]="Margin.XXSMALL"
        [weight]="FontWeight.BOLD"
      >
        What are the terms to end this project?
      </fl-text>
      <fl-bit [flMarginBottom]="Margin.MID">
        <fl-radio [control]="control" [options]="options$ | async"></fl-radio>
      </fl-bit>
    </fl-bit>
    <fl-text
      [color]="FontColor.ERROR"
      [flMarginBottom]="Margin.MID"
      *ngIf="(hasOutstandingPayments$ | async)"
      i18n="Outstanding payments error"
    >
      <!-- has outstanding payment i.e. milestone or invoice -->
      You must release or cancel any outstanding payments before ending your
      project.
    </fl-text>

    <fl-text
      [color]="FontColor.ERROR"
      [flMarginBottom]="Margin.MID"
      *ngIf="(submissionPromise | async) as error"
    >
      <ng-container *ngIf="error.errorCode" [ngSwitch]="error.errorCode">
        <ng-container
          *ngSwitchCase="'PROJECT_PENDING_INVOICE'"
          i18n="Pending invoice error"
        >
          You must release or cancel any oustanding invoices to end your
          project.
        </ng-container>
        <ng-container
          *ngSwitchCase="'PROJECT_PENDING_MILESTONE'"
          i18n="Pending milestone error"
        >
          You must release or cancel any pending milestones to end your project.
        </ng-container>
        <ng-container
          *ngSwitchCase="'PROJECT_PENDING_DISPUTE'"
          i18n="Pending dispute error"
        >
          You cannot end your project until your dispute is resolved.
        </ng-container>
        <ng-container *ngSwitchDefault i18n="Generic error">
          Something went wrong.
        </ng-container>
      </ng-container>
      <ng-container *ngIf="error.requestId" i18n="Give support request ID">
        Please contact support@freelancer.com with this request ID:
        {{ error.requestId }}
      </ng-container>
    </fl-text>
    <fl-bit class="ButtonsContainer">
      <fl-button
        i18n="Button to cancel end project action"
        [color]="ButtonColor.DEFAULT"
        [flMarginRight]="Margin.XXSMALL"
        [size]="ButtonSize.SMALL"
        (click)="handleCancel()"
      >
        Cancel
      </fl-button>
      <fl-button
        i18n="Button to confirm end project action"
        [color]="ButtonColor.SECONDARY"
        [size]="ButtonSize.SMALL"
        [disabled]="!control.dirty || (hasOutstandingPayments$ | async)"
        [busy]="submissionPromise && !(submissionPromise | async)"
        (click)="handleClick()"
      >
        End Project
      </fl-button>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./project-end-modal.component.scss'],
})
export class ProjectEndModalComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  HeadingType = HeadingType;
  HeadingSize = HeadingSize;
  FontColor = FontColor;
  FontWeight = FontWeight;
  Margin = Margin;

  @Input() bidId$: Rx.Observable<number>;

  bidderId: number;
  control = new FormControl();
  options$: Rx.Observable<ReadonlyArray<string>>;

  submissionPromise: Promise<BackendResponse>;
  hasOutstandingPayments$: Rx.Observable<boolean>;

  constructor(
    private datastore: Datastore,
    private modalRef: ModalRef<ProjectEndModalComponent>,
  ) {}

  ngOnInit() {
    const milestones$ = this.datastore
      .collection('milestones', query =>
        query.where('bidId', '==', this.bidId$),
      )
      .valueChanges();

    const invoices$ = this.datastore
      .collection('invoices', query => query.where('bidId', '==', this.bidId$))
      .valueChanges();

    this.options$ = this.getOptions$(milestones$, invoices$);

    this.hasOutstandingPayments$ = this.checkOutstandingPayments$(
      milestones$,
      invoices$,
    );
  }

  handleCancel() {
    this.modalRef.close();
  }

  /**
   * Get options for form input. We use different copy for COMPLETE depending on
   * whether a payment has been made (project is complete) or not (project no
   * longer required).
   */
  private getOptions$(
    milestones$: Rx.Observable<ReadonlyArray<Milestone>>,
    invoices$: Rx.Observable<ReadonlyArray<Invoice>>,
  ) {
    const hasClearedMilestone$ = milestones$.pipe(
      map(milestones =>
        milestones.some(m => m.status === MilestoneStatusApi.CLEARED),
      ),
    );
    const hasPaidInvoice$ = invoices$.pipe(
      map(invoices => invoices.some(i => i.status === InvoiceStatusApi.PAID)),
    );
    const hasMadePayment$ = Rx.combineLatest(
      hasClearedMilestone$,
      hasPaidInvoice$,
    ).pipe(
      map(
        ([hasClearedMilestone, hasPaidInvoice]) =>
          hasClearedMilestone || hasPaidInvoice,
      ),
    );

    return hasMadePayment$.pipe(
      map(hasMadePayment =>
        hasMadePayment
          ? [Reason.COMPLETE, Reason.INCOMPLETE]
          : [Reason.NOT_REQUIRED, Reason.INCOMPLETE],
      ),
    );
  }

  /**
   * Check for outstanding payments (unreleased milestones and unpaid invoices)
   */
  private checkOutstandingPayments$(
    milestones$: Rx.Observable<ReadonlyArray<Milestone>>,
    invoices$: Rx.Observable<ReadonlyArray<Invoice>>,
  ) {
    const unreleasedMilestones$ = milestones$.pipe(
      map(milestones =>
        milestones.some(m => m.status !== MilestoneStatusApi.CLEARED),
      ),
    );
    const unpaidInvoices$ = invoices$.pipe(
      map(invoices => invoices.some(i => i.status !== InvoiceStatusApi.PAID)),
    );
    return Rx.combineLatest(unreleasedMilestones$, unpaidInvoices$).pipe(
      map(
        ([unreleasedMilestones, unpaidInvoices]) =>
          unreleasedMilestones || unpaidInvoices,
      ),
    );
  }
  handleClick() {
    const bidDocument = this.datastore.document('bids', this.bidId$);

    // map from radio option value (which is the displayed copy) to appropriate bid status.
    const bidCompleteStatusOptionsMap: Record<string, BidCompleteStatusApi> = {
      [Reason.COMPLETE]: BidCompleteStatusApi.COMPLETE,
      [Reason.NOT_REQUIRED]: BidCompleteStatusApi.COMPLETE,
      [Reason.INCOMPLETE]: BidCompleteStatusApi.INCOMPLETE,
    };

    const completeStatus = bidCompleteStatusOptionsMap[this.control.value];

    this.submissionPromise = bidDocument
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then(bid => {
        this.bidderId = bid.bidderId;
        return bidDocument.update({ completeStatus });
      })
      .then(bidUpdateResponse => {
        if (bidUpdateResponse.status === 'success') {
          if (completeStatus === BidCompleteStatusApi.COMPLETE) {
            this.modalRef.close(this.bidderId); // complete ending
          } else {
            this.modalRef.close(false); // incomplete ending
          }
        }
        return bidUpdateResponse;
      });
  }
}
